/** Apex hosts that must use the `www.` subdomain for PWA / service worker scope. */
const APEX_HOSTS = new Set(['qualidoc.com.br', 'qualidoc.life']);

/** Only this host may be indexed by search engines. */
const INDEXABLE_HOSTS = new Set(['www.qualidoc.com.br']);

const DEFAULT_CANONICAL_HOST = 'www.qualidoc.com.br';

const BLOCKED_ROBOTS_TXT = `User-agent: *
Disallow: /
`;

export function normalizeHostname(host?: string | null): string {
  return (host ?? '').toLowerCase().split(':')[0];
}

export function getCanonicalSiteHostname(): string {
  const siteUrl = process.env.NEXT_PUBLIC_SITE_URL;
  if (siteUrl) {
    try {
      return new URL(siteUrl).hostname;
    } catch {
      // fall through to default
    }
  }
  return DEFAULT_CANONICAL_HOST;
}

/** Returns the www host when `host` is a known apex domain, otherwise null. */
export function getWwwHostForApex(host: string): string | null {
  const normalized = normalizeHostname(host);
  return APEX_HOSTS.has(normalized) ? `www.${normalized}` : null;
}

/**
 * Request-host check: only `www.qualidoc.com.br` is indexable.
 * Use this in middleware (authoritative for crawlers).
 */
export function isSearchEngineIndexableHost(host?: string | null): boolean {
  return INDEXABLE_HOSTS.has(normalizeHostname(host));
}

/**
 * Build/runtime env check for meta tags and sitemap generation.
 * Blocks indexing when SITE_URL is not production, or when the API
 * endpoint clearly points at homolog/local (safety net if SITE_URL is wrong).
 */
export function isSearchEngineIndexableDeployment(): boolean {
  if (!isSearchEngineIndexableHost(getCanonicalSiteHostname())) {
    return false;
  }

  const apiEndpoint = process.env.NEXT_PUBLIC_REST_API_ENDPOINT ?? '';
  if (/api-hom\.|localhost|127\.0\.0\.1/i.test(apiEndpoint)) {
    return false;
  }

  return true;
}

export function getBlockedRobotsTxt(): string {
  return BLOCKED_ROBOTS_TXT;
}

export function isSitemapPath(pathname: string): boolean {
  return (
    pathname === '/sitemap.xml' ||
    pathname === '/sitemap-static.xml' ||
    pathname === '/sitemap-bulas.xml' ||
    pathname.startsWith('/sitemap-produtos/')
  );
}

/** `/llms.txt` is a production discovery file; homolog hosts must 404 it. */
export function isLlmsTxtPath(pathname: string): boolean {
  return pathname === '/llms.txt' || pathname === '/llms.txt/';
}

/**
 * Service workers are origin-scoped. Registration must run only on the canonical
 * host (e.g. www.qualidoc.com.br), not on apex domains (qualidoc.com.br).
 */
export function isCanonicalPwaHost(hostname?: string): boolean {
  const host = normalizeHostname(
    hostname ?? (typeof window !== 'undefined' ? window.location.hostname : ''),
  );

  if (!host || APEX_HOSTS.has(host)) {
    return false;
  }

  return host === getCanonicalSiteHostname().toLowerCase();
}
